// La fonction 'createAdminService' attend de recevoir l'instance d'api
export const createAdminService = (api) => ({
  baseUrl: "/admin",

  /**
   * Récupérer la liste des utilisateurs avec pagination et filtres
   * @param {Object} params - Paramètres de filtre et pagination
   * @returns {Promise<Object>} - Liste d'utilisateurs et informations de pagination
   */
  async getUsers(params = {}) {
    try {
      const response = await api.get(`${this.baseUrl}/users`, { params });
      return (
        response.data || {
          users: [],
          pagination: { total: 0, page: 1, pages: 1 },
        }
      );
    } catch (error) {
      console.error("Erreur lors de la récupération des utilisateurs:", error);
      // Pour l'instant, on propage une valeur par défaut simple.
      // La gestion d'erreur centralisée pourra être améliorée plus tard.
      return {
        users: [],
        pagination: { total: 0, page: 1, pages: 1 },
      };
    }
  },

  /**
   * Récupérer un utilisateur par son ID
   * @param {string} userId - ID de l'utilisateur
   * @returns {Promise<Object>} - Données de l'utilisateur
   */
  async getUser(userId) {
    try {
      if (!userId) throw new Error("ID utilisateur requis");

      const response = await api.get(`${this.baseUrl}/users/${userId}`);
      return response.data;
    } catch (error) {
      console.error(
        `Erreur lors de la récupération de l'utilisateur ${userId}:`,
        error,
      );
      throw error;
    }
  },

  /**
   * Mettre à jour un utilisateur
   * @param {string} userId - ID de l'utilisateur
   * @param {Object} userData - Données à mettre à jour
   * @returns {Promise<Object>} - Utilisateur mis à jour
   */
  async updateUser(userId, userData) {
    try {
      if (!userId) throw new Error("ID utilisateur requis");

      const response = await api.put(
        `${this.baseUrl}/users/${userId}`,
        userData,
      );
      return response.data;
    } catch (error) {
      console.error(
        `Erreur lors de la mise à jour de l'utilisateur ${userId}:`,
        error,
      );
      throw error;
    }
  },

  /**
   * Met à jour le statut d'un utilisateur (isVerified, status, kycStatus, role)
   * @param {string} userId - ID de l'utilisateur
   * @param {Object} statusData - Données de statut à mettre à jour
   * @returns {Promise<Object>} - Utilisateur mis à jour
   */
  async updateUserStatus(userId, statusData) {
    try {
      if (!userId) throw new Error("ID utilisateur requis");

      const response = await api.patch(
        `${this.baseUrl}/users/${userId}/status`,
        statusData,
      );
      return response.data;
    } catch (error) {
      console.error(
        `Erreur lors de la mise à jour du statut de l'utilisateur ${userId}:`,
        error,
      );
      throw error;
    }
  },

  /**
   * Supprimer un utilisateur
   * @param {string} userId - ID de l'utilisateur
   * @returns {Promise<Object>} - Réponse de confirmation
   */
  async deleteUser(userId) {
    try {
      if (!userId) throw new Error("ID utilisateur requis");

      const response = await api.delete(`${this.baseUrl}/users/${userId}`);
      return response.data;
    } catch (error) {
      console.error(
        `Erreur lors de la suppression de l'utilisateur ${userId}:`,
        error,
      );
      throw error;
    }
  },

  /**
   * Renvoie l'email de vérification pour un utilisateur.
   * @param {string} userId - L'ID de l'utilisateur.
   * @returns {Promise<Object>} - Message de confirmation.
   */
  async resendVerificationEmail(userId) {
    try {
      if (!userId) throw new Error("ID utilisateur requis");

      const response = await api.post(
        `${this.baseUrl}/users/${userId}/resend-verification`,
      );
      return response.data;
    } catch (error) {
      console.error(
        `Erreur lors du renvoi de l'email de vérification pour l'utilisateur ${userId}:`,
        error.response?.data || error.message
      );
      throw error;
    }
  },

  /**
   * Corriger les statuts des utilisateurs vérifiés
   * @returns {Promise<Object>} - Résultat de la migration
   */
  async fixUserStatus() {
    try {
      const response = await api.post(`${this.baseUrl}/users/fix-status`);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la correction des statuts:", error.response?.data || error.message);
      throw error;
    }
  },

  /**
   * Récupérer des statistiques générales
   * @returns {Promise<Object>} - Statistiques du système
   */
  async getStats() {
    try {
      const response = await api.get(`${this.baseUrl}/stats`);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la récupération des statistiques:", error);
      return {
        userCount: 0,
        transactionCount: 0,
        totalVolume: 0,
        activeUsers: 0,
      };
    }
  },

  /**
   * Récupérer les transactions récentes
   * @param {Object} params - Paramètres de filtre et pagination
   * @returns {Promise<Array>} - Liste des transactions
   */
  async getTransactions(params = {}) {
    try {
      const response = await api.get(`${this.baseUrl}/transactions`, {
        params,
      });
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la récupération des transactions:", error);
      return {
        transactions: [],
        pagination: { total: 0, page: 1, pages: 1 },
      };
    }
  },

  async validateTransaction(id) {
    try {
      const response = await api.post(
        `${this.baseUrl}/transactions/${id}/validate`,
      );
      return response.data;
    } catch (error) {
      console.error("Error validating transaction:", error);
      throw error;
    }
  },

  async cancelTransaction(id) {
    try {
      const response = await api.post(
        `${this.baseUrl}/transactions/${id}/cancel`,
      );
      return response.data;
    } catch (error) {
      console.error("Error canceling transaction:", error);
      throw error;
    }
  },

  async getRates() {
    try {
      const response = await api.get("/admin/rates");
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error("Error fetching rates:", error);
      return [];
    }
  },

  async getAuditLogs(params = {}) {
    try {
      const response = await api.get("/admin/audit-logs", { params });
      return response.data || { logs: [], pagination: { total: 0, page: 1, pages: 1 } };
    } catch (error) {
      console.error("Error fetching audit logs:", error);
      return { logs: [], pagination: { total: 0, page: 1, pages: 1 } };
    }
  },

  async createLog(data) {
    try {
      const response = await api.post("/admin/logs", data);
      return response.data;
    } catch (error) {
      console.error("Error creating log:", error);
      throw error;
    }
  },

  /**
   * Récupère les données pour la page des rapports.
   * @param {Object} params - Paramètres de filtre (ex: { period: 'month' }).
   * @returns {Promise<Object>} - Données agrégées pour les rapports.
   */
  async getReportData(params = {}) {
    try {
      const response = await api.get(`${this.baseUrl}/reports`, { params });
      // S'assurer de retourner un objet avec les bonnes clés, même si elles sont vides
      const defaultData = {
        transactionStats: [],
        userStats: [],
        countryData: [],
        volumeByDay: [],
        conversionRates: [],
      };
      return response.data || defaultData;
    } catch (error) {
      console.error("Erreur lors de la récupération des données de rapport:", error);
      // Propager l'erreur pour que le composant puisse l'afficher
      throw error;
    }
  },

  /**
   * Met à jour le statut d'une transaction par un administrateur.
   * @param {string} transactionId - L'ID de la transaction.
   * @param {string} newStatus - Le nouveau statut (ex: 'COMPLETED', 'REJECTED').
   * @param {Object} payload - Données supplémentaires (ex: { adminNotes: "...", manualP2PFee: ... }).
   * @returns {Promise<Object>} La transaction mise à jour.
   */
  async updateTransactionStatus(transactionId, newStatus, payload = {}) {
    try {
      // CORRECTION YOLO : La route est définie dans adminRoutes.js
      // L'URL correcte est /api/admin/transactions/:id/status
      const response = await api.put(
        `/admin/transactions/${transactionId}/status`, 
        { status: newStatus, ...payload }
      );
      return response.data;
    } catch (error) {
      console.error(
        `Erreur lors de la mise à jour du statut de la transaction ${transactionId} vers ${newStatus}:`,
        error.response?.data || error.message
      );
      // Renvoie l'erreur pour qu'elle soit gérée par _updateTransactionStatusApiCall
      // qui affiche déjà un toast. handleApiError pourrait être redondant ici ou nécessiterait
      // de ne pas afficher de toast si _updateTransactionStatusApiCall le fait déjà.
      // Pour l'instant, nous propageons l'erreur.
      throw error; 
    }
  },

  /**
   * Approuve manuellement une transaction.
   * @param {string} transactionId - L'ID de la transaction.
   * @param {string} [notes] - Notes optionnelles.
   * @returns {Promise<Object>} La transaction mise à jour.
   */
  async approveManualTransaction(transactionId, notes = "Transaction approuvée manuellement par un administrateur.") {
    return this.updateTransactionStatus(transactionId, 'MANUALLY_COMPLETED', { adminNotes: notes });
  },

  /**
   * Rejette manuellement une transaction.
   * @param {string} transactionId - L'ID de la transaction.
   * @param {string} [notes] - Notes optionnelles (par exemple, la raison du rejet).
   * @returns {Promise<Object>} La transaction mise à jour.
   */
  async rejectManualTransaction(transactionId, notes = "Transaction rejetée manuellement par un administrateur.") {
    return this.updateTransactionStatus(transactionId, 'REJECTED', { adminNotes: notes });
  },

  /**
   * Récupérer les paramètres de l'application
   */
  async getAppSettings() {
    try {
      const response = await api.get(`${this.baseUrl}/settings`);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la récupération des paramètres de l'application:", error);
      throw error;
    }
  },

  /**
   * Mettre à jour les paramètres de l'application
   * @param {Object} settingsData - Les données de configuration
   */
  async updateAppSettings(settingsData) {
    try {
      const response = await api.post(`${this.baseUrl}/settings`, settingsData);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la mise à jour des paramètres de l'application:", error);
      throw error;
    }
  },
  
  async getAlerts(params = {}) {
    try {
      const response = await api.get(`${this.baseUrl}/alerts`, { params });
      return response.data || { alerts: [], pagination: {} };
    } catch (error) {
      console.error("Erreur lors de la récupération des alertes:", error);
      throw error;
    }
  },

  async updateAlertStatus(alertId, status, notes = '') {
    try {
      return await api.put(`${this.baseUrl}/alerts/${alertId}/status`, { status, notes });
    } catch (error) {
      console.error(`Erreur lors de la mise à jour de l'alerte ${alertId}:`, error);
      throw error;
    }
  },
  
  async deleteAlert(alertId) {
    try {
      return await api.delete(`${this.baseUrl}/alerts/${alertId}`);
    } catch (error) {
      console.error(`Erreur lors de la suppression de l'alerte ${alertId}:`, error);
      throw error;
    }
  },
  
  async getBlockedIPs(params = {}) {
    try {
      const response = await api.get(`${this.baseUrl}/firewall/blocked-ips`, { params });
      return response.data || { blockedIPs: [], pagination: {} };
    } catch (error) {
      console.error("Erreur lors de la récupération des IPs bloquées:", error);
      throw error;
    }
  },

  async blockIP(ipData) { // ipData: { ipAddress: string, reason: string, durationHours?: number }
    try {
      return await api.post(`${this.baseUrl}/firewall/block-ip`, ipData);
    } catch (error) {
      console.error(`Erreur lors du blocage de l'IP ${ipData.ipAddress}:`, error);
      throw error;
    }
  },
  
  async unblockIP(blockedIpId) {
    try {
      return await api.delete(`${this.baseUrl}/firewall/unblock-ip/${blockedIpId}`);
    } catch (error) {
      console.error(`Erreur lors du déblocage de l'IP (ID: ${blockedIpId}):`, error);
      throw error;
    }
  },
});
